"use client"

// A short-lived, client-only ring buffer of "what did the user just do" —
// used ONLY to help Glym's feedback-escalation flow understand what led up
// to a reported bug. Nothing here is ever sent to the server on its own; it
// only gets attached to a chat message when the user is actively talking to
// Glym, and only ends up persisted server-side if a real feedback report is
// submitted. Deliberately shallow: method/path/status for network calls, no
// request/response bodies, so nothing sensitive (tokens, other users' data,
// payment payloads) can end up in it.

export interface ActivityBreadcrumb {
  time: string // ISO timestamp — matches the shape components/admin/feedback-manager.tsx already renders
  action: string
}

const MAX_BREADCRUMBS = 40
const MAX_AGE_MS = 90_000

let buffer: ActivityBreadcrumb[] = []
let installed = false

function record(action: string) {
  const now = Date.now()
  buffer.push({ time: new Date(now).toISOString(), action: action.slice(0, 160) })
  const cutoff = now - MAX_AGE_MS
  buffer = buffer.filter((b) => new Date(b.time).getTime() >= cutoff).slice(-MAX_BREADCRUMBS)
}

/** Call once, as early as possible (root layout) — idempotent. */
export function installActivityBreadcrumbs() {
  if (installed || typeof window === "undefined") return
  installed = true

  // Network breadcrumbs — method + path + status only, never bodies.
  const originalFetch = window.fetch.bind(window)
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const [input, init] = args
    const method = (init?.method || (typeof input !== "string" && !(input instanceof URL) ? (input as Request).method : "GET") || "GET").toUpperCase()
    let path = ""
    try {
      const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request).url
      path = new URL(url, window.location.origin).pathname
    } catch {
      path = "unknown"
    }
    try {
      const res = await originalFetch(...args)
      record(`${method} ${path} → ${res.status}`)
      return res
    } catch (err) {
      record(`${method} ${path} → network error`)
      throw err
    }
  }

  // Click breadcrumbs — nearest interactive element's visible label only.
  document.addEventListener(
    "click",
    (e) => {
      const target = (e.target as HTMLElement)?.closest?.("button, a, [role='button']") as HTMLElement | null
      if (!target) return
      const label = (target.getAttribute("aria-label") || target.innerText || target.getAttribute("title") || "").trim().replace(/\s+/g, " ").slice(0, 60)
      if (label) record(`Clicked "${label}"`)
    },
    { capture: true }
  )

  // Error breadcrumbs — message only, no stack traces (could contain paths).
  window.addEventListener("error", (e) => record(`Error: ${String(e.message || "unknown").slice(0, 120)}`))
  window.addEventListener("unhandledrejection", (e) => record(`Unhandled rejection: ${String(e.reason?.message || e.reason || "unknown").slice(0, 120)}`))
}

/** Record a route change — call from a client component watching usePathname(). */
export function recordNavigation(path: string) {
  record(`Navigated to ${path}`)
}

export function getRecentBreadcrumbs(): ActivityBreadcrumb[] {
  const cutoff = Date.now() - MAX_AGE_MS
  return buffer.filter((b) => new Date(b.time).getTime() >= cutoff)
}
